/**
 * DroppableProductRow - Detects external file drops on product rows
 *
 * Wraps a TableRow with HTML5 drag events for file drops from desktop.
 * Only activates for files (not internal dnd-kit drags).
 */

import { useState, useCallback, useRef } from 'react'
import { TableRow } from '@/components/v2/ui'
import { cn } from '@/lib/utils'

interface DroppableProductRowProps {
  children: React.ReactNode
  onFileDrop?: (files: File[]) => void
  selectable?: boolean
  selected?: boolean
  onClick?: () => void
  className?: string
}

export function DroppableProductRow({
  children,
  onFileDrop,
  selectable,
  selected,
  onClick,
  className,
}: DroppableProductRowProps) {
  const [isDragOver, setIsDragOver] = useState(false)
  const dragCounterRef = useRef(0)

  const hasFiles = (e: React.DragEvent) =>
    Array.from(e.dataTransfer.types).includes('Files')

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    if (!hasFiles(e) || !onFileDrop) return
    dragCounterRef.current++
    setIsDragOver(true)
  }, [onFileDrop])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    if (hasFiles(e)) e.dataTransfer.dropEffect = 'copy'
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    dragCounterRef.current--
    if (dragCounterRef.current === 0) setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      dragCounterRef.current = 0
      setIsDragOver(false)
      const files = Array.from(e.dataTransfer.files).filter((f) =>
        f.type.startsWith('image/')
      )
      if (files.length > 0) onFileDrop?.(files)
    },
    [onFileDrop]
  )

  return (
    <TableRow
      selectable={selectable}
      selected={selected}
      onClick={onClick}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        className,
        isDragOver &&
          'ring-2 ring-inset ring-primary-500 bg-primary-50/50 dark:bg-primary-900/20'
      )}
    >
      {children}
    </TableRow>
  )
}
